import React, { useState } from 'react';
import { Modal, Button, Input, Badge } from '@/components/ui';
import type { AssetHealthStatus, AssetHealthLog } from '../types/assets.types';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';

interface AssetHealthLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  assetId: string;
  assetName: string;
  onSave: (log: Partial<AssetHealthLog>) => Promise<void>;
}

const healthOptions: { value: AssetHealthStatus; label: string; color: string }[] = [
  { value: 'good', label: 'Khỏe mạnh (Good)', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'warning', label: 'Cần chú ý (Warning)', color: 'bg-amber-100 text-amber-700' },
  { value: 'bad', label: 'Vấn đề / Sâu bệnh (Bad)', color: 'bg-red-100 text-red-700' },
];

export function AssetHealthLogModal({ isOpen, onClose, assetId, assetName, onSave }: AssetHealthLogModalProps) {
  const { profile } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<AssetHealthLog>>({
    asset_id: assetId,
    health_status: 'good',
    disease_note: '',
    pest_note: '',
    action_taken: '',
    checked_by: profile?.id || '',
    checked_at: new Date().toISOString(),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await onSave(formData);
      toast.success('Cập nhật sức khỏe thành công');
      onClose();
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Cập nhật sức khỏe: ${assetName}`}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6 py-2">
        <div>
          <label className="block text-sm font-bold text-slate-700 mb-3">Tình trạng sức khỏe *</label>
          <div className="grid grid-cols-3 gap-3">
            {healthOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setFormData({ ...formData, health_status: opt.value })}
                className={`p-4 rounded-xl border-2 transition-all text-center ${formData.health_status === opt.value
                    ? 'border-os-primary bg-os-primary/5 ring-2 ring-os-primary/20'
                    : 'border-slate-100 hover:border-slate-200 bg-white'
                  }`}
              >
                <div className={`w-10 h-10 rounded-full mx-auto mb-2 flex items-center justify-center ${opt.color}`}>
                  <i className={`ri-${opt.value === 'good' ? 'heart-pulse-line' : opt.value === 'warning' ? 'error-warning-line' : 'virus-line'} text-xl`} />
                </div>
                <p className="text-[11px] font-bold  tracking-tight">{opt.label.split(' ')[0]}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Mô tả sâu bệnh / Vấn đề</label>
            <textarea
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-3 min-h-[80px]"
              placeholder="Mô tả chi tiết tình trạng cây, triệu chứng sâu bệnh nếu có..."
              value={formData.disease_note || ''}
              onChange={(e) => setFormData({ ...formData, disease_note: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Ghi chú loài sâu bệnh (Pest)</label>
            <Input
              value={formData.pest_note || ''}
              onChange={(e) => setFormData({ ...formData, pest_note: e.target.value })}
              placeholder="VD: Rầy nâu, nấm lá..."
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Hành động khắc phục / Xử lý</label>
            <textarea
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-3 min-h-[80px]"
              placeholder="VD: Phun thuốc trừ sâu, bón thêm phân, cắt tỉa cành bệnh..."
              value={formData.action_taken || ''}
              onChange={(e) => setFormData({ ...formData, action_taken: e.target.value })}
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-50">
          <Button variant="outline" type="button" onClick={onClose} disabled={isLoading}>Hủy</Button>
          <Button variant="primary" type="submit" isLoading={isLoading}>Lưu cập nhật</Button>
        </div>
      </form>
    </Modal>
  );
}
